import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bedtime companion hook:
 *  - Fetches the last saved bookmark and offers a "Resume" prompt.
 *  - Saves a new bookmark as the active chapter index advances.
 *  - Auto-fades narration + ambient scene volume to zero over 20 minutes when
 *    bedtime mode is on and playback is active.
 *
 * All side-effects (state setters for volume / sceneVolume) are passed in as
 * callbacks so the parent remains the single source of truth.
 */
export default function useBedtime({
  isOpen,
  user,
  hasChapter,
  verse,
  activeVerse,
  chapterIdx,
  scope,
  bedtimeMode,
  isPlaying,
  volume,
  sceneVolume,
  onVolumeChange,
  onSceneVolumeChange,
}) {
  const [resumeBookmark, setResumeBookmark] = useState(null);
  const fadeTimerRef = useRef(null);
  const lastBookmarkSentRef = useRef(null);

  // Fetch last bookmark when dialog opens with a chapter loaded.
  useEffect(() => {
    if (!isOpen || !user || !hasChapter || !verse) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/bedtime/bookmark`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const bm = data?.bookmark;
        if (!bm) return;
        const chapRefFromVerse = (verse.reference || '').replace(/:\d+$/, '');
        const bmChap = bm.chapter_ref || (bm.verse_reference || '').replace(/:\d+$/, '');
        if (chapRefFromVerse && bmChap && chapRefFromVerse === bmChap) {
          setResumeBookmark(bm);
        }
      } catch (_) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasChapter]);

  // Persist bookmark as the chapter progresses.
  useEffect(() => {
    if (!user || scope !== 'chapter' || !hasChapter || !activeVerse) return;
    const key = `${activeVerse.reference}::${chapterIdx}`;
    if (lastBookmarkSentRef.current === key) return;
    lastBookmarkSentRef.current = key;
    const chapterRef = (verse?.reference || activeVerse.reference || '').replace(/:\d+$/, '');
    fetch(`${API}/api/bedtime/bookmark`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        verse_reference: activeVerse.reference,
        verse_text: activeVerse.text,
        chapter_ref: chapterRef,
        chapter_idx: chapterIdx,
        translation: activeVerse.translation || null,
      }),
    }).catch(() => { /* non-critical */ });
  }, [activeVerse, chapterIdx, scope, hasChapter, user, verse]);

  // 20-minute auto-fade while bedtime is on + playback active.
  useEffect(() => {
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (!bedtimeMode || !isPlaying) return;
    const startedAt = Date.now();
    const TOTAL_MS = 20 * 60 * 1000;
    const startVol = volume;
    const startScene = sceneVolume;
    fadeTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min(1, elapsed / TOTAL_MS);
      onVolumeChange(Math.max(0, startVol * (1 - pct)));
      onSceneVolumeChange(Math.max(0, startScene * (1 - pct)));
      if (pct >= 1) {
        clearInterval(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    }, 5000);
    return () => {
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedtimeMode, isPlaying]);

  const clearFade = () => {
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  };

  return { resumeBookmark, setResumeBookmark, clearFade };
}
